import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Grid,
  Typography,
  Box,
  Button,
  Divider,
  Alert,
  Paper,
  Stack,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentUserData } from "src/features/auth/authSlice";
import { useUpdatePasswordMutation } from "src/features/users/usersApiSlice";
import Iconify from "src/components/iconify/Iconify";
import PasswordTextField from "../../../components/form/fields/PasswordTextField";
import {
  getFailedPasswordPolicyMessages,
  getPasswordPolicyChecks,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_POLICY_RULES,
} from "src/utils/passwordPolicy";

const NewPasswordSettings = () => {
  const currentUserData = useSelector(selectCurrentUserData);
  const [updatePassword, { isLoading }] = useUpdatePasswordMutation();
  const [submitFeedback, setSubmitFeedback] = useState(null);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const newPassword = watch("newPassword") || "";

  const criteriaState = useMemo(() => {
    const checks = getPasswordPolicyChecks(newPassword);
    return PASSWORD_POLICY_RULES.map((criterion) => ({
      ...criterion,
      fulfilled: checks[criterion.key],
    }));
  }, [newPassword]);

  const onSubmit = async (data) => {
    setSubmitFeedback(null);
    clearErrors();

    if (!data?.oldPassword || !data?.newPassword || !data?.confirmPassword) {
      if (!data?.oldPassword) {
        setError("oldPassword", {
          type: "manual",
          message: "Bitte aktuelles Passwort eingeben.",
        });
      }
      if (!data?.newPassword) {
        setError("newPassword", {
          type: "manual",
          message: "Bitte neues Passwort eingeben.",
        });
      }
      if (!data?.confirmPassword) {
        setError("confirmPassword", {
          type: "manual",
          message: "Bitte neues Passwort bestätigen.",
        });
      }

      setSubmitFeedback({
        type: "error",
        message: "Bitte füllen Sie alle Felder aus.",
      });
      return;
    }

    const failedCriteria = getFailedPasswordPolicyMessages(data.newPassword);

    if (failedCriteria.length > 0) {
      setError("newPassword", {
        type: "manual",
        message: PASSWORD_POLICY_MESSAGE,
      });
      setSubmitFeedback({
        type: "error",
        message: `Bitte prüfen Sie das neue Passwort: ${failedCriteria.join(", ")}.`,
      });
      return;
    }

    if (data.confirmPassword !== data.newPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Passwörter stimmen nicht überein.",
      });
      setSubmitFeedback({
        type: "error",
        message: "Die neuen Passwörter stimmen nicht überein.",
      });
      return;
    }

    try {
      const response = await updatePassword({
        userId: currentUserData?.id,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      }).unwrap();

      setSubmitFeedback({
        type: "success",
        message: response?.message || "Passwort wurde erfolgreich geändert.",
      });
      reset();
    } catch (error) {
      const status = error?.status || error?.originalStatus;
      const backendMessage = error?.data?.message;

      if (status === 401) {
        setError("oldPassword", {
          type: "manual",
          message: "Das aktuelle Passwort ist nicht korrekt.",
        });
      }

      setSubmitFeedback({
        type: "error",
        message:
          backendMessage ||
          (status === 401
            ? "Das aktuelle Passwort ist nicht korrekt."
            : "Passwort konnte nicht geändert werden. Bitte erneut versuchen."),
      });
    }
  };

  return (
    <>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Passwort
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Typography variant="h6">Passwort ändern</Typography>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              bgcolor: "background.default",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Passwort-Anforderungen
            </Typography>
            <Stack spacing={0.75}>
              {criteriaState.map((criterion) => (
                <Box
                  key={criterion.message}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Iconify
                    icon={
                      criterion.fulfilled
                        ? "eva:checkmark-circle-2-fill"
                        : "eva:close-circle-fill"
                    }
                    width={18}
                    sx={{
                      color: criterion.fulfilled ? "success.main" : "text.disabled",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {criterion.message}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {submitFeedback ? (
            <Alert severity={submitFeedback.type}>{submitFeedback.message}</Alert>
          ) : null}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <PasswordTextField
                  name="oldPassword"
                  control={control}
                  label="Aktuelles Passwort"
                  fullWidth
                />
                {errors.oldPassword ? (
                  <Typography variant="caption" color="error" sx={{ pl: 1 }}>
                    {errors.oldPassword.message}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12} md={4}>
                <PasswordTextField
                  name="newPassword"
                  control={control}
                  label="Neues Passwort"
                  fullWidth
                />
                {errors.newPassword ? (
                  <Typography variant="caption" color="error" sx={{ pl: 1 }}>
                    {errors.newPassword.message}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12} md={4}>
                <PasswordTextField
                  name="confirmPassword"
                  control={control}
                  label="Neues Passwort wiederholen"
                  fullWidth
                />
                {errors.confirmPassword ? (
                  <Typography variant="caption" color="error" sx={{ pl: 1 }}>
                    {errors.confirmPassword.message}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                disabled={isLoading}
                sx={{ mt: 2, minWidth: 200 }}
              >
                {isLoading ? "Wird gespeichert..." : "Passwort ändern"}
              </Button>
            </Box>
          </form>
        </Stack>
      </Paper>
    </>
  );
};

export default NewPasswordSettings;
